define([ // eslint-disable-line import/no-amd
  'underscore',
  'es6!sumo/util',
  'es6!156085c5-0017-4150-b225-a731ad248f38/service/form',
  'es6!launcher/util/listbuilder/constants',
], (
  _, Util, ListBuilderForm, ListBuilderConstants,
) => {
  const { FormTypes, FormGoals, InlineClassnames } = ListBuilderConstants;
  const { FEATURES } = Util.Constants;

  class ListBuilderPopup extends ListBuilderForm {
    constructor(service, settings) {
      super(service, settings);

      this.isAnimating = false;
      this.isClickEmitted = false;
      this.isFormSubmitted = false;
      this.isBlackoutClassActive = false;
      this.overlayTouchTarget = false;

      this.isSocialProof = this.settings.get('goal') === FormGoals.SOCIAL_PROOF;
      this.toggleEmbeddedBlackoutOverlay = this.toggleEmbeddedBlackoutOverlay.bind(this);
    }

    // Create the inline popup containers and insert them into the page
    installEmbedded() {
      const hasTrackingPixels = this.hasFeature(FEATURES.TRACKING_PIXELS);
      const popupId = this.settings.get('id');
      const embedPlacement = this.settings.get('auto_embed_placement');
      const isEmbedded = this.settings.get('formType') === FormTypes.INLINE_POPUP;
      const isAutoEmbedded = isEmbedded && this.settings.get('embed_mode') === 'auto';

      // Create the background blackout container, utilized if user enabled the 'Inline Focus' option
      this.blackoutOverlayEl = document.createElement('div');
      this.blackoutOverlayEl.className = 'sumome-popup-embed-overlay';

      // Store the blackout status for determining blackout popup tracking on later scroll event
      this.embedBlackoutStatus = this.settings.get('embed_blackout_status');

      // Create the outermost popup container
      this.popupEl = document.createElement('div');
      this.popupEl.className = 'sumo-form-wrapper listbuilder-popup listbuilder-popup-embedded';

      // If user is pro, add margin for badge placement so that badge does not overflow page text
      if (!hasTrackingPixels) this.popupEl.style = 'margin-bottom: 52px';

      // Create the main popup content container (will wrap popup scaling properties)
      if (!this.isSocialProof) {
        this.contentEl = document.createElement('div');
        this.contentEl.className = 'listbuilder-popup-content';
        this.popupEl.appendChild(this.contentEl);
      }
      // Insert the popup into the page
      this.popupEl = this.anchorEl.parentNode.insertBefore(this.popupEl, (isAutoEmbedded && embedPlacement > 50 && this.anchorEl.nextSibling) ? this.anchorEl.nextSibling : this.anchorEl);

      // Insert the background blackout container immediately above the outermost popup container
      this.blackoutOverlayEl = this.popupEl.parentNode.insertBefore(this.blackoutOverlayEl, this.popupEl);

      // Initialize the embedded blackout function
      this.toggleEmbeddedBlackoutOverlay();

      this.showStep(this.currentStep || 'subscribe');
    }

    /**
     * Toggle the embedded popup's sibling overlay class (for appropriate background-color).
     * @param {Object} overlay - The current popup's overlay sibling element.
     * @param {string} previousClass - The already existing overlay background class (if present).
     * @param {string} newClass - The new class to be applied to the current overlay.
     */
    toggleEmbeddedBlackoutClass(overlayEl, previousClass, newClass) {
      if (overlayEl.classList.contains(previousClass)) {
        overlayEl.classList.remove(previousClass);
      }
      overlayEl.classList.add(newClass);
    }

    // For inline popups with the Inline Focus option (dark) enabled, toggle the overlay blackout class
    toggleEmbeddedBlackoutOverlay() {
      const screenHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      const midpoint = screenHeight / 2;

      /**
       * Run through each popup element and store all properties of inline forms on scroll event.
       * Used for determining whether or not embed-blackout or embed-light classes should be applied.
       * Give the function a reference to the class so that it may access popups in this.service.
       * @return {Array} - An array of embedded popups currently in the viewport.
       */
      const getInlinePopupsProps = () => {
        const popupsInViewportArr = [];

        this.service.service.services.forEach((service) => {
          if ('popup' in service &&  service.popup && service.popup.formType === FormTypes.INLINE_POPUP) {
            const popup = service.popup;
            const overlayEl = popup.blackoutOverlayEl;
            const popupEl = popup.popupEl;
            const height = popupEl.offsetHeight;
            const position = popupEl.getBoundingClientRect();
            const top = position.top;
            const bottom = position.bottom;
            const embedBlackoutStatus = popup.embedBlackoutStatus;

            if (embedBlackoutStatus === 'dark' && isPopupInViewport(top, bottom)) {
              popupsInViewportArr.push({overlayEl, popupEl, position, height, top, bottom});
            }
          }
        });
        return popupsInViewportArr;
      }

      // Determine whether or not the popup being evaulated is in the viewport.
      const isPopupInViewport = (top, bottom) => (top > 0 && bottom < screenHeight);

      // Determine whether or not the "embed-blackout" class should be applied to the current popup.
      const shouldApplyBlackout = (popup) => (popup.top <= (midpoint - (popup.height/2)));

      // Get all props for popups in viewport, save in array of objects and toggle classes as needed.
      const trackEmbeddedBlackoutPopups = () => {
        const popupsInViewport = getInlinePopupsProps();

        // If there is no active blackout class, apply the 'embed-blackout' class to the first popup
        // to appear in the viewport, save the popup as active and set the blackout active status
        if (!this.isBlackoutClassActive) {
          if (popupsInViewport.length >= 1) {
            this.activeInlinePopupEl = popupsInViewport[0];
            if (shouldApplyBlackout(this.activeInlinePopupEl)) {
              this.toggleEmbeddedBlackoutClass(this.activeInlinePopupEl.overlayEl, InlineClassnames.LIGHT, InlineClassnames.DARK);
              this.isBlackoutClassActive = true;
            }
          }
        }

        // If there is a currently active popup (with blackout class applied), but there are no
        // more popups in the viewport, remove the active status of the last saved popup
        if (this.activeInlinePopupEl && !popupsInViewport.length) {
          this.toggleEmbeddedBlackoutClass(this.activeInlinePopupEl.overlayEl, InlineClassnames.DARK, InlineClassnames.LIGHT);
          this.isBlackoutClassActive = false;
        }
      }

      // Execute tracking function on initial function execution in case inline popup with blackout
      // status enabled is already in the viewport & should have overlay applied
      setTimeout(() => trackEmbeddedBlackoutPopups());

      // Execute the embedded blackout popup tracking with throttle to assist performance.
      this.service.addEvent(window, 'scroll', _.throttle(trackEmbeddedBlackoutPopups, 125));
    }
  }

  return ListBuilderPopup;
});
